import type {
  FeatureServiceBinder,
  FeatureServiceProviderDefinition,
  FeatureServices,
  Logger,
  SharedFeatureService,
} from '@feature-hub/core';
import {readFromStorage, writeToStorage} from './storage.js';
import type {
  CloseCalculatorCallbackV1,
  ConfigChangeListenerV1,
  OpenCalculatorListenerV1,
  PaymentConfigServiceV1,
  PaymentConfigV1,
} from './v1.js';
import {PaymentConfigMode} from './v1.js';

export * from './v1.js';

export interface SharedPaymentConfigService extends SharedFeatureService {
  readonly '1.0.0': FeatureServiceBinder<PaymentConfigServiceV1>;
}

export interface PaymentConfigServiceDependencies extends FeatureServices {
  readonly 's2:logger'?: Logger;
}

export const paymentConfigServiceDefinition: FeatureServiceProviderDefinition<
  SharedPaymentConfigService,
  PaymentConfigServiceDependencies
> = {
  id: `payment-config-service`,

  optionalDependencies: {
    featureServices: {
      's2:logger': `^1.0.0`,
    },
  },

  create: ({featureServices}) => {
    const {'s2:logger': logger = console} = featureServices;
    const configChangeListeners = new Set<[ConfigChangeListenerV1, string[]]>();
    let loadCalculatorListener: (() => void) | undefined;
    let openCalculatorListener: OpenCalculatorListenerV1 | undefined;
    let {config, leasingParams, params} = readFromStorage(logger);

    const pendingOpenCalculatorListenerParamsQueue: Parameters<OpenCalculatorListenerV1>[] =
      [];

    return {
      '1.0.0': (consumerId) => {
        return {
          featureService: {
            getConfig: () => config,

            setConfig: async (paymentConfig: PaymentConfigV1) => {
              config = paymentConfig;
              if (paymentConfig.mode === PaymentConfigMode.FINANCE) {
                params = paymentConfig.params;
              } else if (paymentConfig.mode === PaymentConfigMode.LEASING) {
                leasingParams = paymentConfig.leasingParams;
                params = paymentConfig.params;
              }

              writeToStorage({config, leasingParams, params}, logger);

              for (const [onConfigChange] of configChangeListeners) {
                const configChange = onConfigChange({
                  consumerId,
                  purchaseItem: {type: `none`},
                });
                if (configChange instanceof Promise) {
                  await configChange;
                }
              }
            },

            subscribe: (listener, options) => {
              const l: [ConfigChangeListenerV1, string[]] = [
                listener,
                options?.matchers ?? [`submit`],
              ];
              configChangeListeners.add(l);

              return createUnsubscribe(
                () => configChangeListeners.delete(l),
                options,
              );
            },

            registerCalculatorLoader: (listener, options) => {
              loadCalculatorListener = listener;

              void Promise.resolve().then(() => {
                if (
                  loadCalculatorListener === listener &&
                  pendingOpenCalculatorListenerParamsQueue.length > 0
                ) {
                  loadCalculatorListener();
                }
              });

              return createUnsubscribe(() => {
                if (loadCalculatorListener === listener) {
                  loadCalculatorListener = undefined;
                }
              }, options);
            },

            registerCalculator: (listener, options) => {
              openCalculatorListener = listener;

              void Promise.resolve().then(() => {
                do {
                  if (openCalculatorListener === listener) {
                    const _params =
                      pendingOpenCalculatorListenerParamsQueue.shift();

                    if (_params) {
                      openCalculatorListener(..._params);
                    }
                  } else {
                    break;
                  }
                } while (pendingOpenCalculatorListenerParamsQueue.length > 0);
              });

              return createUnsubscribe(() => {
                if (openCalculatorListener === listener) {
                  openCalculatorListener = undefined;
                }
              }, options);
            },

            openCalculator: ({purchaseItem, initialConfig = config}) => {
              const onConfigChangeCb: CloseCalculatorCallbackV1 = async (
                newConfig,
                event,
              ) => {
                config = newConfig;

                if (config.mode === PaymentConfigMode.FINANCE) {
                  params = config.params;
                } else if (config.mode === PaymentConfigMode.LEASING) {
                  leasingParams = config.leasingParams;
                  params = config.params;
                }

                for (const [
                  onConfigChange,
                  matchers,
                ] of configChangeListeners) {
                  if (!matchers.includes(event)) {
                    continue;
                  }
                  const configChange = onConfigChange({
                    consumerId,
                    purchaseItem,
                  });
                  if (configChange instanceof Promise) {
                    await configChange;
                  }
                }

                if (event === `submit`) {
                  writeToStorage({config, leasingParams, params}, logger);
                }
              };

              const calcParams: Parameters<OpenCalculatorListenerV1> = [
                {
                  purchaseItem,
                  calculatorConfig: initialConfig && {
                    leasingParams,
                    params,
                    ...initialConfig,
                  },
                },
                (p) => onConfigChangeCb(p, `submit`),
                onConfigChangeCb,
              ];

              if (openCalculatorListener) {
                openCalculatorListener(...calcParams);
              } else {
                pendingOpenCalculatorListenerParamsQueue.push(calcParams);

                loadCalculatorListener?.();
              }
            },
          },
        };
      },
    };
  },
};

function createUnsubscribe(
  onUnsubscribe: () => void,
  options: {readonly signal?: AbortSignal} = {},
): () => void {
  options.signal?.addEventListener(`abort`, onUnsubscribe);

  return onUnsubscribe;
}
